import { FC, SyntheticEvent, useContext, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { ActionRow, GETListControlFilter, ListControlFilterForm, ModalType } from 'src/types'
import { FormInput, ModalWrapper } from '@microservices/wiskey-react-components'
import { Box } from '@mui/material'

import { AddActionDialog } from '@pages/FormCreateOrEdit/components/FieldsConfig/ConfiguredElementsOnForm/AddActionDialog'
import { FieldsConfigContext } from '@pages/FormCreateOrEdit/components/FieldsConfig/FieldsConfig'
import { getCommandsFormattedResponse } from '@pages/GanttChart/helpers'

import { CommandPickerController } from '@components/hookFormControllers/CommandPickerController'
import { InputWithListVariables } from '@components/InputWithListVariables'
import { SwitchTable } from '@components/SwitchTable'
import { Tab, TabPanel, Tabs } from '@components/Tabs'

import { useDeleteActionMutation, useFetchActionMutation } from '@redux/api'

import { usePrompt } from '@hooks'
import { GENERATOR_INPUT_TYPE, MODAL_TYPE, MODEL_TYPE, VIEW_ACTION_COLUMNS } from '@constants'

type OuterParametersDialogProps = {
  entityId: number
  currentOuterParametersData: GETListControlFilter | null
  objectCode: string
  isShow: boolean
  onClose: (isDirty: boolean) => void
  onSubmit: (filter: ListControlFilterForm) => void
}

export const OuterParametersDialog: FC<OuterParametersDialogProps> = ({
  entityId,
  isShow,
  currentOuterParametersData,
  objectCode,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation()

  const { formId } = useContext(FieldsConfigContext)

  const [currentTab, setCurrentTab] = useState<number>(0)
  const [actionDialogModalType, setActionDialogModalType] = useState<ModalType>(MODAL_TYPE.CREATE)
  const [showActionDialog, setShowActionDialog] = useState<boolean>(false)
  const [currentAction, setCurrentAction] = useState<ActionRow | null>(null)

  const methods = useForm<ListControlFilterForm>({
    defaultValues: { value: '', title: '', commands: [], actions: [] },
  })
  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty },
  } = methods
  const watchId = watch('id')
  const watchActions = watch('actions')

  const [fetchAction] = useFetchActionMutation()
  const [deleteAction] = useDeleteActionMutation()

  const handleTabChange = (event: SyntheticEvent, value: number) => setCurrentTab(value)

  const handleOpenActionModal = (type: ModalType, id?: string | number) => {
    if (type === MODAL_TYPE.EDIT && id) {
      fetchAction(id).then(action => {
        if ('data' in action) setCurrentAction(action.data)
      })
    }

    setShowActionDialog(true)
    setActionDialogModalType(type)
  }

  const handleCloseActionModal = (isDirty?: boolean) => {
    if (isDirty && confirm(i18next.t('notifications.leave'))) {
      setShowActionDialog(false)
      setCurrentAction(null)

      return
    }
    if (!isDirty) {
      setShowActionDialog(false)
      setCurrentAction(null)
    }
  }

  const handleUpdateAction = (action: ActionRow, modalType: ModalType) => {
    const actions = watchActions

    if (modalType === MODAL_TYPE.EDIT) {
      const index = watchActions.findIndex(act => act.id === action.id)

      if (index !== -1) {
        actions[index] = action
        setValue('actions', actions, { shouldDirty: true })

        return
      }
    }

    actions.unshift(action)
    setValue('actions', actions, { shouldDirty: true })
  }

  const handleDeleteAction = (id: number | string) => {
    deleteAction(id).then(() => {
      setValue(
        'actions',
        watchActions.filter(action => action.id !== id),
        { shouldDirty: true }
      )
    })
  }

  useEffect(() => {
    if (currentOuterParametersData) {
      reset({
        ...currentOuterParametersData,
        commands: getCommandsFormattedResponse(currentOuterParametersData.commands, []),
      })
    }
  }, [currentOuterParametersData])

  usePrompt({ when: isDirty })

  const actionTable = { rows: watchActions, columns: VIEW_ACTION_COLUMNS }

  return (
    <ModalWrapper
      hideDivider
      disabledSave={!isDirty}
      isShow={isShow}
      title={t('fieldsConfig.outerParameters.dialog.title', { type: watchId ? 'Edit' : 'Add' })}
      width={1000}
      containerStyle={{
        px: 1,
        py: 1,
        // borderRadius: 0,
        height: 600,
      }}
      onClose={() => onClose(isDirty)}
      onSave={handleSubmit(onSubmit)}
    >
      <Box height={500} mb={0.25} mt={1}>
        <AddActionDialog
          actionTable={actionTable}
          currentAction={currentAction}
          entityId={entityId}
          modalType={actionDialogModalType}
          outerParametersId={watchId}
          showDialog={showActionDialog}
          onCloseModal={handleCloseActionModal}
          onUpdateActions={handleUpdateAction}
        />
        <FormProvider {...methods}>
          <FormInput
            inputType={GENERATOR_INPUT_TYPE.INPUT}
            label={t('fieldsConfig.outerParameters.dialog.labels.title')}
            name='title'
            placeholder={t('fieldsConfig.outerParameters.dialog.placeholder.title')}
            rules={{ required: true, validate: value => Boolean(value.trim('')) }}
          />
          <Box sx={{ pt: 0 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={currentTab} onChange={handleTabChange}>
                <Tab
                  label={`${t(`fieldsConfig.outerParameters.dialog.tabs.filter`)}`}
                  sx={{ pl: 0 }}
                  tabIndex={0}
                />
                <Tab
                  label={`${t(`fieldsConfig.outerParameters.dialog.tabs.actions`)}`}
                  sx={{ pl: 0 }}
                  tabIndex={1}
                />
                <Tab
                  label={`${t(`fieldsConfig.outerParameters.dialog.tabs.commands`)}`}
                  sx={{ pl: 0 }}
                  tabIndex={2}
                />
              </Tabs>
            </Box>
            <TabPanel
              contentSx={{ paddingX: 0, height: '400px', overflow: 'auto' }}
              index={0}
              value={currentTab}
            >
              <InputWithListVariables
                formId={formId}
                name={'value'}
                placeholder={t('fieldsConfig.outerParameters.dialog.placeholder.filter')}
              />
            </TabPanel>
            <TabPanel index={1} value={currentTab}>
              <SwitchTable
                disablePagination
                isCrud
                btnDisabled={!watchId}
                btnText={t(`configEntity.form.actions.addButton`)}
                columns={VIEW_ACTION_COLUMNS}
                rows={watchActions}
                showActionsColumn={true}
                showDialog={showActionDialog}
                type={MODEL_TYPE.FORM_CONFIG_LIST_CONTROL_ELEMENT_ACTION}
                containerSx={{
                  '.MuiTableBody-root > div': {
                    height: '280px !important',
                  },
                }}
                onDelete={handleDeleteAction}
                onEdit={params => handleOpenActionModal(MODAL_TYPE.EDIT, params.row.original.id)}
                onShowDialog={handleOpenActionModal}
              />
            </TabPanel>
            <TabPanel
              index={2}
              value={currentTab}
              contentSx={{
                pl: 0,
                pb: 1,
                pr: 1,
                overflow: 'auto',
                height: 400,
              }}
            >
              <CommandPickerController
                isAnotherObjectWithRequestData
                commandName='name'
                objectCode={objectCode}
              />
            </TabPanel>
          </Box>
        </FormProvider>
      </Box>
    </ModalWrapper>
  )
}
